import logo from './logo.svg';
import './App.css';
import React, { useEffect, useLayoutEffect, useState } from 'react';
import { Route, Routes, useLocation } from "react-router-dom";
import WalletContextProvider from "./WalletProvider";
import MuultiNavbar from "./components/MuultiNavbar";
import MuultiPage from "./components/pages/MuultiPage";
import PublicMuultiPage from "./components/pages/PublicMuultiPage";
import UserListPage from "./components/pages/UserListPage";
import MuultiFooter from "./components/MuultiFooter";
import GateListsAdminPage from "./components/pages/GateListsAdminPage";
import CreateCollectionPage from "./components/pages/CreateCollectionPage";
import AboutMuultiPage from "./components/pages/AboutMuultiPage";
import MintNFTStudioPage from "./components/pages/MintNFTStudioPage";
// App.js (near the top)
import './components/WalletOverrides.css';
import TextComponentDetailPage from "./components/pages/TextComponentDetailPage";
import VideoComponentDetailPage from "./components/pages/VideoComponentDetailPage";
import LinkComponentDetailPage from "./components/pages/LinkComponentDetailPage";
import ImageComponentDetailPage from "./components/pages/ImageComponentDetailPage";
import MintNFTComponentDetailPage from "./components/pages/MintNFTComponentDetailPage";
import DonationComponentDetailPage from "./components/pages/DonationComponentDetailPage";
import PrivacyPolicy from "./components/pages/PrivacyPolicy";
import LicenseTerms from "./components/pages/LicenseTerms";
import CopyrightPolicy from "./components/pages/CopyrightPolicy";
import MuultiSpaceLogFeed from "./components/pages/MuultiSpaceLogFeed";
import MuultiStreet3DPage from "./components/pages/MuultiPage3DPage";
import MuultiSpace3DDetailPage from "./components/pages/MuultiSpace3DDetailPage";
import MuultiSpaceAdminDashboard from "./components/pages/MuultiSpaceAdminDashboard";
import ComponentTypeListPage from "./components/pages/ComponentTypeListPage";
import UserWalletDetailPage from "./components/pages/UserWalletDetailPage";
import UserFollowersPage from "./components/pages/UserFollowersPage";
import AdComposerPage from "./components/pages/AdComposerPage";
import CreatorAdMarketPage from "./components/pages/CreatorAdMarketPage";
import { resetPageMetadata } from './utils/pageMetadata';
import { getPageRoute } from '../functions/publicMetadataPolicy.js';
import { NotFoundPage } from './components/pages/PageStatus';
import useHomepageUsage from './hooks/useHomepageUsage';

const APP_ROUTES = new Set([
  "muulti-directory",
  "token-gates",
  "about",
  "collection-creation",
  "nft-studio",
  "posts",
  "wallet-detail",
  "3d",
  "muultispace-admin",
  "legal",
  "ads",
]);

const PUBLIC_COMPONENT_SEGMENTS = new Set(["c", "v", "l", "i", "m", "d", "list"]);
const THEME_STORAGE_KEY = "muulti-theme-preference";
const REVEAL_COMPONENT_SELECTOR = [
  ".component-grid-item",
  ".component-item",
  ".benefit-card",
  ".featured-card",
  ".homepage-shortcut-tile",
  ".user-directory-item",
].join(",");

function isPublicSpaceRoute(pathname) {
  const clean = (pathname || "/").split(/[?#]/)[0];
  const parts = clean.split("/").filter(Boolean);
  if (parts.length === 0) return false;
  const first = parts[0];
  if (APP_ROUTES.has(first)) return false;
  const secondLast = parts[parts.length - 2];
  if (PUBLIC_COMPONENT_SEGMENTS.has(secondLast)) return true;
  return parts.length <= 2;
}

function readThemePreference() {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    if (raw === "light" || raw === "dark") return raw;
  } catch {}
  return null;
}

function App() {
  const location = useLocation();
  const [themePreference, setThemePreference] = useState(() => readThemePreference());

  useHomepageUsage(location);

  useLayoutEffect(() => {
    resetPageMetadata(location.pathname);
  }, [location.pathname]);

  useLayoutEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return undefined;
    if (!("IntersectionObserver" in window) || !("MutationObserver" in window)) return undefined;

    const body = document.body;
    const observed = new WeakSet();
    body.classList.add("muulti-reveal-ready");

    const revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("muulti-reveal-visible");
          revealObserver.unobserve(entry.target);
        });
      },
      {
        root: null,
        rootMargin: "0px 0px -8% 0px",
        threshold: 0.08,
      }
    );

    const shouldObserve = (element) => {
      if (!(element instanceof HTMLElement)) return false;
      if (element.classList.contains("component-grid-item")) return true;
      return !element.closest(".component-grid-item");
    };

    const observeRevealTargets = (root = document) => {
      root.querySelectorAll(REVEAL_COMPONENT_SELECTOR).forEach((element) => {
        if (observed.has(element) || !shouldObserve(element)) return;
        observed.add(element);
        element.classList.add("muulti-reveal-item");
        revealObserver.observe(element);
      });
    };

    observeRevealTargets();

    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          if (node.matches(REVEAL_COMPONENT_SELECTOR) && shouldObserve(node) && !observed.has(node)) {
            observed.add(node);
            node.classList.add("muulti-reveal-item");
            revealObserver.observe(node);
          }
          observeRevealTargets(node);
        });
      });
    });

    mutationObserver.observe(document.getElementById("root") || body, {
      childList: true,
      subtree: true,
    });

    return () => {
      mutationObserver.disconnect();
      revealObserver.disconnect();
      body.classList.remove("muulti-reveal-ready");
    };
  }, []);

  useEffect(() => {
    const handleThemeChange = () => setThemePreference(readThemePreference());
    window.addEventListener("muulti-theme-change", handleThemeChange);
    window.addEventListener("storage", handleThemeChange);
    return () => {
      window.removeEventListener("muulti-theme-change", handleThemeChange);
      window.removeEventListener("storage", handleThemeChange);
    };
  }, []);

  useEffect(() => {
    const isPublic = isPublicSpaceRoute(location.pathname);
    const preferred = themePreference === "light" || themePreference === "dark" ? themePreference : null;
    const root = document.documentElement;
    const body = document.body;
    if (isPublic) {
      const usePublicDark = preferred === "dark";
      root.classList.toggle("theme-dark", false);
      body.classList.toggle("theme-dark", false);
      root.classList.toggle("public-theme-dark", usePublicDark);
      body.classList.toggle("public-theme-dark", usePublicDark);
    } else {
      const nextTheme = preferred || "dark";
      root.classList.toggle("public-theme-dark", false);
      body.classList.toggle("public-theme-dark", false);
      root.classList.toggle("theme-dark", nextTheme === "dark");
      body.classList.toggle("theme-dark", nextTheme === "dark");
    }
  }, [location.pathname, themePreference]);

  return (
<>

    <MuultiNavbar/>
        <div className="muulti-body-container">
            {getPageRoute(location.pathname).kind === 'missing' ? <NotFoundPage /> : <Routes>
                    <Route path="/" element={<MuultiPage />}/>
                    <Route path="/muultispace-admin" element={<MuultiSpaceAdminDashboard />} />
                    <Route path="/wallet-detail" element={<UserWalletDetailPage />} />
                    <Route path="/wallet-detail/followers" element={<UserFollowersPage />} />
                    <Route path="/ads/compose" element={<AdComposerPage />} />
                    <Route path="/ads/market" element={<CreatorAdMarketPage />} />
                    <Route path="/:username" element={<PublicMuultiPage />} />
                    <Route path="/:username/list/:componentType" element={<ComponentTypeListPage />} />
                    <Route path="/muulti-directory" element={<UserListPage/>}/>
                    <Route path="/token-gates" element={<GateListsAdminPage/>}/>
                    <Route path="/about" element={<AboutMuultiPage />} />
                    <Route path="/collection-creation" element={<CreateCollectionPage/>}/>
                    <Route path="/nft-studio" element={<MintNFTStudioPage />} />
                    <Route path="/:username/c/:componentId" element={<TextComponentDetailPage />} />
                    <Route path="/:username/v/:componentId" element={<VideoComponentDetailPage />} />
                    <Route path="/:username/l/:componentId" element={<LinkComponentDetailPage />} />
                    <Route path="/:username/i/:componentId" element={<ImageComponentDetailPage />} />
                    <Route path="/:username/m/:componentId" element={<MintNFTComponentDetailPage />} />
                    <Route path="/:username/d/:componentId" element={<DonationComponentDetailPage />} />
                    <Route path="/posts" element={<MuultiSpaceLogFeed />} />
                    <Route path="/posts/:postId" element={<MuultiSpaceLogFeed />} />
                    <Route path="/3d/street" element={<MuultiStreet3DPage />} />
                    <Route path="/3d/space/:username" element={<MuultiSpace3DDetailPage />} />
                    <Route path="/legal/privacy" element={<PrivacyPolicy />} />
                    <Route path="/legal/license" element={<LicenseTerms />} />
                    <Route path="/legal/copyright" element={<CopyrightPolicy />} />
                    <Route path="*" element={<NotFoundPage />} />

            </Routes>}
        </div>
    <MuultiFooter/>

</>
          );
}

export default App;
